"use client";

import React, { useState } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Globe, Smartphone, Database, Cpu, Building2, HelpCircle, Loader2, Sparkles, MessageCircle } from "lucide-react";
import { DiscoveryCategory } from "./ContextualDiscoveryModal";

interface Act4BoardroomProps {
  onOpenDiscoveryModal: (category?: DiscoveryCategory) => void;
}

export const Act4Boardroom: React.FC<Act4BoardroomProps> = ({ onOpenDiscoveryModal }) => {
  const [selectedCategory, setSelectedCategory] = useState<DiscoveryCategory>("custom");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    scope: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const OPTIONS: { id: DiscoveryCategory; label: string; icon: React.ElementType }[] = [
    { id: "website", label: "WEBSITE", icon: Globe },
    { id: "mobile", label: "MOBILE APP", icon: Smartphone },
    { id: "crm", label: "CRM / ERP", icon: Database },
    { id: "automation", label: "AI / AUTOMATION", icon: Cpu },
    { id: "custom", label: "CUSTOM SOFTWARE", icon: Building2 },
    { id: "notsure", label: "NOT SURE YET", icon: HelpCircle },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        service: `Boardroom Consultation: ${selectedCategory.toUpperCase()}`,
        message: `Project Scope:\n${formData.scope || "Consultation requested from final boardroom scene."}`,
        source: "Boardroom Climax Scene",
      };

      await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      setIsSubmitted(true);
    } catch {
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="scene-boardroom"
      className="relative min-h-screen w-full flex flex-col justify-center py-24 px-6 sm:px-8 lg:px-16 bg-slate-900 text-[#111318] overflow-hidden border-t border-[#111318]/10"
    >
      {/* Real Architectural Photograph: Executive Glass Boardroom in Natural Daylight (100% Crisp, Full-Bleed) */}
      <div className="absolute inset-0 z-0">
          <Image
            src="/images/storytelling/office/meeting-room.webp"
            alt="Johnson Softwares Executive Glass Boardroom"
            fill
            sizes="100vw"
            quality={85}
            loading="lazy"
            className="object-cover object-center"
          />
      </div>

      {/* Subtle, localized radial fade behind table & form to ensure sharp text contrast (Leaves 75%+ of photo untouched) */}
      <div className="absolute inset-0 bg-gradient-to-t from-white/95 via-white/80 to-transparent pointer-events-none z-[1]" />

      <div className="relative z-10 w-full max-w-4xl mx-auto pt-10">
        {/* Narrator Typography Sitting in Natural Negative Space */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10 max-w-2xl mx-auto"
        >
          <span className="text-xs font-mono tracking-widest text-[#2563EB] uppercase block mb-2 font-semibold">
            EXECUTIVE CONSULTATION
          </span>
          <h2 className="text-4xl sm:text-6xl font-extrabold text-[#111318] tracking-tight leading-tight mb-3">
            SO...
            <br />
            <span className="text-[#2563EB]">
              WHAT ARE YOU BUILDING?
            </span>
          </h2>
          <p className="text-base text-[#374151] max-w-lg mx-auto leading-relaxed">
            You&apos;ve walked through our headquarters, explored our engineering studios, and inspected the systems we deploy. Take a seat at the table and tell us what you need.
          </p>
        </motion.div>

        {/* Clean Requirement Choices */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2.5 mb-8">
          {OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const isSelected = selectedCategory === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setSelectedCategory(opt.id)}
                className={`p-4 rounded-xl border text-xs font-mono font-bold tracking-wider flex flex-col items-center justify-center gap-2 transition-all ${
                  isSelected
                    ? "bg-[#2563EB] border-[#2563EB] text-white shadow-md shadow-[#2563EB]/25 scale-105"
                    : "bg-white/95 border-[#111318]/15 text-[#374151] hover:text-[#111318] hover:border-[#111318]/30 shadow-xs"
                }`}
              >
                <Icon className="w-5 h-5" />
                <span>{opt.label}</span>
              </button>
            );
          })}
        </div>

        {/* Contextual Requirement Form Anchored Cleanly to the Boardroom Space */}
        <div className="rounded-3xl bg-white/95 border border-[#111318]/10 p-8 sm:p-10 shadow-[0_20px_50px_rgb(0,0,0,0.08)]">
          {isSubmitted ? (
            <div className="text-center py-10 space-y-4">
              <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 flex items-center justify-center mx-auto mb-2">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold text-[#111318]">Your Seat at the Table is Reserved</h3>
              <p className="text-sm text-[#5E636B] max-w-md mx-auto leading-relaxed">
                Our principal software architect is reviewing your {selectedCategory.toUpperCase()} parameters and will contact you directly with an initial technical blueprint.
              </p>
              <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="https://wa.me/919880893416?text=Hi%20Johnson%20Softwares,%20I%20just%20submitted%20a%20project%20inquiry%20via%20the%20virtual%20tour."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold flex items-center gap-2 shadow-xs transition-all"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>Connect Instantly on WhatsApp</span>
                </a>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="flex items-center justify-between pb-3 border-b border-[#111318]/10 text-xs font-mono text-[#5E636B]">
                <span className="font-semibold text-[#111318]">BOARDROOM CONSULTATION</span>
                <button
                  type="button"
                  onClick={() => onOpenDiscoveryModal(selectedCategory)}
                  className="text-[#2563EB] hover:underline flex items-center gap-1 font-semibold"
                >
                  <span>Open Deep Questionnaire</span>
                  <ArrowRight className="w-3 h-3" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-mono text-[#5E636B] mb-1 font-medium">Your Name *</label>
                  <input
                    required
                    type="text"
                    placeholder="e.g. Vikram Singh"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-[#F8F8F6] border border-[#111318]/10 rounded-xl px-4 py-2.5 text-sm text-[#111318] focus:outline-none focus:border-[#2563EB] focus:bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono text-[#5E636B] mb-1 font-medium">Company *</label>
                  <input
                    required
                    type="text"
                    placeholder="e.g. Acme Corp"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full bg-[#F8F8F6] border border-[#111318]/10 rounded-xl px-4 py-2.5 text-sm text-[#111318] focus:outline-none focus:border-[#2563EB] focus:bg-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-mono text-[#5E636B] mb-1 font-medium">Work Email *</label>
                  <input
                    required
                    type="email"
                    placeholder="vikram@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-[#F8F8F6] border border-[#111318]/10 rounded-xl px-4 py-2.5 text-sm text-[#111318] focus:outline-none focus:border-[#2563EB] focus:bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono text-[#5E636B] mb-1 font-medium">Phone / WhatsApp *</label>
                  <input
                    required
                    type="tel"
                    placeholder="+91 98765 43210"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full bg-[#F8F8F6] border border-[#111318]/10 rounded-xl px-4 py-2.5 text-sm text-[#111318] focus:outline-none focus:border-[#2563EB] focus:bg-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-mono text-[#5E636B] mb-1 font-medium">
                  What is your core operational requirement or software goal?
                </label>
                <textarea
                  rows={3}
                  placeholder="Tell us about the problems you want to solve or systems you want to automate..."
                  value={formData.scope}
                  onChange={(e) => setFormData({ ...formData, scope: e.target.value })}
                  className="w-full bg-[#F8F8F6] border border-[#111318]/10 rounded-xl px-4 py-2.5 text-sm text-[#111318] focus:outline-none focus:border-[#2563EB] focus:bg-white resize-none"
                />
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-3">
                <div className="flex items-center gap-2 text-xs text-[#5E636B]">
                  <Sparkles className="w-4 h-4 text-[#2563EB] shrink-0" />
                  <span>Direct engineering review. No aggressive sales reps.</span>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-[#2563EB] hover:bg-blue-700 text-white font-semibold text-sm transition-all shadow-[0_4px_16px_rgba(37,99,235,0.3)] flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Transmitting Blueprint...</span>
                    </>
                  ) : (
                    <>
                      <span>SUBMIT FOR ARCHITECTURAL REVIEW</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
